import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Download, Loader2, Users, UserCheck, UserPlus } from 'lucide-react';

interface EnterpriseRouteInterestRow {
  id: string;
  full_name: string | null;
  email: string;
  phone: string | null;
  user_id: string | null;
  created_at: string;
  programs: { name: string } | null;
}

function toCsv(rows: EnterpriseRouteInterestRow[]): string {
  const header = ['Nombre', 'Correo', 'Celular', 'Ruta', 'Registrado', 'Fecha'];
  const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const lines = rows.map(r =>
    [
      r.full_name ?? '',
      r.email,
      r.phone ?? '',
      r.programs?.name ?? '',
      r.user_id ? 'Sí' : 'No',
      new Date(r.created_at).toLocaleString('es-CO'),
    ].map(esc).join(','),
  );
  return [header.map(esc).join(','), ...lines].join('\n');
}

export function EnterpriseRouteInterestsTab({ organizationId }: { organizationId: string }) {
  const isValidOrg = !!organizationId && organizationId !== 'all';

  const { data, isLoading } = useQuery({
    queryKey: ['enterprise-route-interests', organizationId],
    queryFn: async (): Promise<EnterpriseRouteInterestRow[]> => {
      const { data, error } = await supabase
        .from('route_interests')
        .select('id, full_name, email, phone, user_id, created_at, programs(name)')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as EnterpriseRouteInterestRow[];
    },
    enabled: isValidOrg,
  });

  const rows = data ?? [];
  const registered = rows.filter(r => !!r.user_id).length;

  const handleExport = () => {
    const blob = new Blob(['\uFEFF' + toCsv(rows)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'interesados-en-rutas.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isValidOrg) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Selecciona una empresa específica para ver sus interesados en rutas.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <Users className="w-8 h-8 text-primary" />
            <div>
              <p className="text-2xl font-bold">{rows.length}</p>
              <p className="text-xs text-muted-foreground">Interesados totales</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <UserCheck className="w-8 h-8 text-primary" />
            <div>
              <p className="text-2xl font-bold">{registered}</p>
              <p className="text-xs text-muted-foreground">Ya registrados</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <UserPlus className="w-8 h-8 text-primary" />
            <div>
              <p className="text-2xl font-bold">{rows.length - registered}</p>
              <p className="text-xs text-muted-foreground">Sin registrar</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Interesados del equipo
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Personas de tu organización que marcaron “Estoy Interesado” en una ruta.
            </p>
          </div>
          <Button variant="outline" onClick={handleExport} disabled={rows.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Aún no hay interesados en rutas en esta organización.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Correo</TableHead>
                    <TableHead>Celular</TableHead>
                    <TableHead>Ruta</TableHead>
                    <TableHead>Registrado</TableHead>
                    <TableHead>Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.full_name || '—'}</TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>
                        {r.phone ? (
                          <a href={`https://wa.me/${r.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline whitespace-nowrap">
                            {r.phone}
                          </a>
                        ) : '—'}
                      </TableCell>
                      <TableCell>{r.programs?.name ?? '—'}</TableCell>
                      <TableCell>
                        <Badge variant={r.user_id ? 'default' : 'secondary'}>
                          {r.user_id ? 'Sí' : 'No'}
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(r.created_at).toLocaleString('es-CO')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
